import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

import { Column, Row, Text } from "@/components";
import { HexagonIconBadge } from "@/components/leaderboard/HexagonIconBadge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import type { HexGradient, LeaderboardUser } from "@/types";

// 1st place is marked with the category's hex badge; 2nd/3rd get medals.
const PLACE_MEDALS = ["🥈", "🥉"];

export const MetricLeadersHoverCard = ({
  title,
  icon,
  gradient,
  leaders,
  valueOf,
  format,
  firstMarker,
  isDark = false,
  badgeBackdropColor,
  children,
}: {
  title: string;
  icon: LucideIcon;
  gradient: HexGradient;
  // Already sorted highest-first, at most 3.
  leaders: LeaderboardUser[];
  valueOf: (user: LeaderboardUser) => number;
  format: (value: number) => string;
  firstMarker?: ReactNode;
  isDark?: boolean;
  badgeBackdropColor?: string;
  children: ReactNode;
}) => {
  /* Render */
  const renderMarker = (rank: number) => {
    if (rank === 0 && firstMarker !== undefined) {
      return (
        <Row className="h-8 w-7 items-center justify-center">{firstMarker}</Row>
      );
    }
    if (rank === 0) {
      return (
        <HexagonIconBadge
          icon={icon}
          gradient={gradient}
          size="table"
          backdropColor={badgeBackdropColor}
        />
      );
    }
    return (
      <Row className="h-8 w-7 items-center justify-center">
        <Text className="text-base leading-none">{PLACE_MEDALS[rank - 1]}</Text>
      </Row>
    );
  };

  const renderRow = (user: LeaderboardUser, rank: number) => (
    <Row key={user.userId} className="items-center gap-2">
      {renderMarker(rank)}
      <Text
        truncate
        className={cn(
          "min-w-0 flex-1 text-sm font-medium",
          isDark ? "text-slate-100" : "text-slate-900",
        )}
      >
        {user.firstName} {user.lastName}
      </Text>
      <Text
        className={cn(
          "shrink-0 text-sm font-semibold tabular-nums",
          isDark ? "text-white" : "text-slate-900",
        )}
      >
        {format(valueOf(user))}
      </Text>
    </Row>
  );

  const render = () => (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side="top"
        className={cn("w-60 p-0", isDark && "border-white/15 bg-slate-950/95")}
      >
        <Column>
          <Text
            className={cn(
              "border-b px-3 py-2 text-xs font-semibold uppercase tracking-wider",
              isDark
                ? "border-white/10 text-slate-400"
                : "border-slate-100 text-slate-500",
            )}
          >
            {title}
          </Text>
          <Column className="gap-1.5 p-3">
            {leaders.length === 0 ? (
              <Text
                className={cn(
                  "text-sm",
                  isDark ? "text-slate-500" : "text-slate-400",
                )}
              >
                No data yet
              </Text>
            ) : (
              leaders.map((user, rank) => renderRow(user, rank))
            )}
          </Column>
        </Column>
      </TooltipContent>
    </Tooltip>
  );

  return render();
};
